import type { BootstrapBundle } from "../setup/bootstrap-bundle-types.js";
import {
  startWorkbenchServer,
  type WorkbenchServerHandle,
  type WorkbenchServerOptions,
} from "../workbench/server.js";
import { buildChildEnv, decodeJwtClaims } from "./bundle.js";
import { defaultGit, ensureDir, type GitPrepDeps } from "./git-prep.js";
import { pollBootstrapBundle } from "./poll.js";
import { defaultExec } from "./exec.js";
import { startWorkspaceSshd } from "./sshd.js";
import type { BootContext } from "./types.js";
import { reclaimHomeOwnership, wireUserHome } from "./user-home.js";
import { cloneReferenceRepos, runPreReadyBinds } from "./workspace-extras.js";

/**
 * The injectable step surface of `runWorkbenchBoot` (workbench-boot.ts) plus
 * its production defaults — split out so tests exercise the boot sequencing
 * without network, sockets, or real git, and so the composition module stays
 * within the dependency budget.
 */
export interface WorkbenchBootDeps {
  /** Task 4 poll — throws BootstrapUnauthorizedError on 401 (fatal), resolves
   *  null when aborted (SIGTERM during standby). */
  pollBundle(ctx: BootContext): Promise<BootstrapBundle | null>;
  /** Task 3 unpack — claims decode + child env. */
  unpack(ctx: BootContext, bundle: BootstrapBundle): Record<string, string>;
  startDaemon(opts: WorkbenchServerOptions): Promise<WorkbenchServerHandle>;
  gitPrepDeps(ctx: BootContext, bundle: BootstrapBundle): GitPrepDeps;
  /** GitPrepJob.onReady — graphify bind + grimoire submodule + skill links. */
  bindsBeforeReady(ctx: BootContext, bundle: BootstrapBundle): Promise<void>;
  /** GitPrepJob.afterReady — reference repos, never blocking Claude. */
  cloneReferences(ctx: BootContext, bundle: BootstrapBundle): Promise<void>;
  /** mkdir -p /workspaces/repo before git prep — the daemon serves shell cwd
   *  from it, and the no-git-plan prep path never creates it (bash did so
   *  unconditionally, entrypoint.sh:848). */
  ensureWorkspaceDir(ctx: BootContext): void;
  wireHome(ctx: BootContext): void;
  startSshd(ctx: BootContext): Promise<void>;
  reclaimOwnership(ctx: BootContext): Promise<void>;
  /** Daemon lifetime — resolves never; SIGTERM → exit is Task 9's wiring. */
  park(ctx: BootContext): Promise<never>;
}

const WORKSPACE_DIR = "/workspaces/repo";
const HOME_DIR = "/home/conveyor";
const USER_HOME_MOUNT = "/mnt/conveyor-users";

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export function defaultWorkbenchBootDeps(): WorkbenchBootDeps {
  return {
    pollBundle: (ctx) =>
      pollBootstrapBundle(ctx.apiUrl, ctx.bootstrapToken, {
        fetchFn: fetch,
        sleepFn: sleep,
        log: ctx.log,
      }),
    unpack: (ctx, bundle) => {
      const claims = decodeJwtClaims(bundle.sessionJwt);
      ctx.claims = claims;
      return buildChildEnv({
        base: process.env,
        bundle,
        claims,
        hostname: ctx.hostname,
        log: ctx.log,
      });
    },
    startDaemon: (opts) => startWorkbenchServer(opts),
    gitPrepDeps: (ctx, bundle) => ({
      git: defaultGit,
      bundle,
      podImage: process.env.CONVEYOR_POD_IMAGE === "1",
      log: ctx.log,
    }),
    bindsBeforeReady: (ctx, bundle) => runPreReadyBinds(WORKSPACE_DIR, bundle, defaultGit, ctx.log),
    cloneReferences: (ctx, bundle) =>
      cloneReferenceRepos(bundle.envVars?.REFERENCE_REPOS_JSON, defaultGit, ctx.log),
    ensureWorkspaceDir: (ctx) => {
      ensureDir(WORKSPACE_DIR);
      ctx.log.info(`[boot] ensured workspace dir ${WORKSPACE_DIR}`);
    },
    wireHome: (ctx) => {
      // Same id sources as the bash (entrypoint.sh:342-343, 388): userId from
      // the pod env / bundle envVars, projectId from the claim with a pod-env
      // fallback — all merged into childEnv by the unpack step.
      wireUserHome({
        home: HOME_DIR,
        userHomeMount: USER_HOME_MOUNT,
        userId: ctx.childEnv?.CONVEYOR_USER_ID,
        projectId: ctx.childEnv?.CONVEYOR_PROJECT_ID ?? ctx.childEnv?.PROJECT_ID,
        sharedDir: process.env.CONVEYOR_SHARED_DIR,
        log: ctx.log,
      });
    },
    startSshd: (ctx) => startWorkspaceSshd(defaultExec, ctx.log),
    reclaimOwnership: (ctx) => reclaimHomeOwnership(HOME_DIR, defaultExec, ctx.log),
    park: () =>
      new Promise<never>(() => {
        /* parked for the daemon's lifetime */
      }),
  };
}
